import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";
import { colors } from "../../config/theme";
import { tdStyle, thStyle } from "../../helpers/constants";
import { IconAction } from "../../helpers/general";
import { useCookie } from "../../hooks/useCookie";
import apiService from "../../service/apiService";

interface TableProps {
  publications: Publication[];
  refetch: () => void;
}

const publicationName = (p: Publication) =>
  p.event?.name ?? p.offer?.title ?? p.place?.name ?? null;

export default function AdvertisementTable({
  publications,
  refetch,
}: TableProps) {
  const { token } = useCookie("vToken");
  const [toDelete, setToDelete] = useState<Publication | null>(null);

  const deleteItem = useMutation(
    (id: string) => apiService.MakeDeleteRequest(`publications/${id}`, token),
    {
      onSuccess: () => {
        toast.success("Item deleted successfully!");
        refetch();
      },
      onError: () => {
        toast.error("Error deleting item");
      },
    },
  );

  return (
    <>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Date</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Catégorie</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Nom</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Actif</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Classement</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {publications?.map((publication) => {
            const name = publicationName(publication);
            return (
              <CTableRow key={publication.id}>
                <CTableDataCell style={tdStyle}>
                  {new Date(publication.createdAt).toLocaleDateString()}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  {publication.relatedType}
                </CTableDataCell>

                <CTableDataCell
                  style={{
                    ...tdStyle,
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name ? (
                    <span className="fw-semibold">{name}</span>
                  ) : (
                    <a
                      href={publication.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {publication.url}
                    </a>
                  )}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  {publication.isActive ? "✅" : "❌"}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  {publication.order}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <IconAction
                      title="Modifier"
                      to={`/advertisement/update/${publication.id}`}
                    >
                      <EditIcon
                        width="16"
                        height="16"
                        fill={colors.secondary}
                      />
                    </IconAction>
                    <IconAction
                      title="Supprimer"
                      danger
                      onClick={() => setToDelete(publication)}
                    >
                      <DeleteIcon
                        width="18"
                        height="18"
                        fill={colors.primary}
                      />
                    </IconAction>
                  </div>
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>

      {/* One delete modal for the whole table */}
      <PromptModal
        open={toDelete !== null}
        onAccept={async () => {
          if (toDelete) {
            try {
              await deleteItem.mutateAsync(toDelete.id);
            } finally {
              setToDelete(null);
            }
          }
        }}
        onClose={() => setToDelete(null)}
      />
    </>
  );
}
